import * as React from 'react';
import { Row, Col } from 'reactstrap';
import { accentUtils } from '../../services/HelperService';
import AccentIcons from '../../icons/Icons';
import { AccentButton } from '../AccentButton';
import { AccentComboUnbound } from '../unbound/AccentComboUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { AccentNumberUnbound } from '../unbound/AccentNumberUnbound';
import { AccentCheckUnbound } from '../unbound/AccentCheckUnbound';
import { filterOps } from './AdvancedFilterConstants';
import { getQueryProductList, getQueryProductOptions } from './InsyteQueryProductOptionService';

// ── Field ID encoding ────────────────────────────────────────────────────────
// Lines.Option:{productId}:{optionId}:{valueType}
// e.g. Lines.Option:3f2e1d4c-...:7a9b2c1d-...:Text

export const CONFIG_OPTION_SENTINEL = 'Lines.Option';

export function encodeConfigOptionField(productId, optionId, valueType) {
    return `${CONFIG_OPTION_SENTINEL}:${productId}:${optionId}:${valueType}`;
}

export function decodeConfigOptionField(field) {
    if (!field || !field.startsWith(CONFIG_OPTION_SENTINEL + ':')) return null;
    const parts = field.split(':');
    // field format: Lines.Option:{productId}:{optionId}:{valueType}
    // but GUIDs also contain colons in the form xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    // So we encode as: Lines.Option:productGuid:optionGuid:valueType
    if (parts.length < 4) return null;
    return {
        productId: parts[1] ?? null,
        optionId: parts[2] ?? null,
        valueType: parts[3] ?? 'Raw'
    };
}

// ── Operator lists per value type ────────────────────────────────────────────

const TEXT_OPS = [
    { id: filterOps.eq,         label: 'Equals' },
    { id: filterOps.ne,         label: 'Not equals' },
    { id: filterOps.startswith, label: 'Starts with' },
    { id: filterOps.endswith,   label: 'Ends with' },
    { id: filterOps.isempty,    label: 'Is empty' },
    { id: filterOps.isnotempty, label: 'Is not empty' }
];
const NUM_OPS = [
    { id: filterOps.eq,  label: '=' },
    { id: filterOps.ne,  label: '≠' },
    { id: filterOps.gt,  label: '>' },
    { id: filterOps.ge,  label: '≥' },
    { id: filterOps.lt,  label: '<' },
    { id: filterOps.le,  label: '≤' },
    { id: filterOps.isempty,    label: 'Is empty' },
    { id: filterOps.isnotempty, label: 'Is not empty' }
];
const BOOL_OPS = [
    { id: filterOps.eq,  label: 'Is' },
    { id: filterOps.ne,  label: 'Is not' }
];
const PICKLIST_OPS = [
    { id: filterOps.eq,         label: 'Equals' },
    { id: filterOps.ne,         label: 'Not equals' },
    { id: filterOps.isempty,    label: 'Is empty' },
    { id: filterOps.isnotempty, label: 'Is not empty' }
];

function opsForType(valueType) {
    switch (valueType) {
        case 'Numeric':  return NUM_OPS;
        case 'Boolean':  return BOOL_OPS;
        case 'Picklist': return PICKLIST_OPS;
        default:         return TEXT_OPS;
    }
}

// ── Main component ───────────────────────────────────────────────────────────

/**
 * Replaces the standard predicate row for the Lines.Option sentinel field.
 *
 * Layout:
 *   [Product ▼]  [Option Group > Option ▼]  [op ▼]  [value input]  [×]
 *
 * item.field = Lines.Option:{productId}:{optionId}:{valueType}
 * item.op    = the selected operator
 * item.value = the filter value
 */
export const InsyteQueryConfigOptionPredicateCtrl = React.memo(props => {

    const { item, readOnly, onItemChanged, onDelete, supplierKey } = props;

    const [actionsVisible, setActionsVisible] = React.useState(false);

    // Parse current state from field ID
    const decoded = decodeConfigOptionField(item.field);

    console.log('[ConfigOptionPredicate] render — item.field:', item.field, '| decoded:', decoded);

    // ── Product loading ─────────────────────────────────────────────────────
    const [products, setProducts] = React.useState([]);

    React.useEffect(() => {
        console.log('[ConfigOptionPredicate] loading product list');
        getQueryProductList({ supplierKey }).then(list => {
            console.log('[ConfigOptionPredicate] product list loaded:', list?.length);
            setProducts(list ?? []);
        });
    }, [supplierKey]);

    // ── Option loading (when product selected) ──────────────────────────────
    const [optionGroups, setOptionGroups] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(false);

    React.useEffect(() => {
        console.log('[ConfigOptionPredicate] options effect — productId:', decoded?.productId);
        if (!decoded?.productId) {
            setOptionGroups([]);
            setOptionsLoading(false);
            return;
        }
        setOptionsLoading(true);
        setOptionGroups([]);
        getQueryProductOptions(decoded.productId).then(groups => {
            console.log('[ConfigOptionPredicate] options loaded:', groups?.length, 'groups');
            setOptionGroups(groups ?? []);
            setOptionsLoading(false);
        }).catch((e) => {
            console.error('[ConfigOptionPredicate] options load failed:', e);
            setOptionGroups([]);
            setOptionsLoading(false);
        });
    }, [decoded?.productId]);

    // Flatten options for the combo
    const flatOptions = React.useMemo(() =>
        optionGroups.flatMap(g =>
            (g.options ?? []).map(o => ({
                ID: `${o.optionID}|${o.optionType}`,
                Text: g.groupName ? `${g.groupName} › ${o.optionName}` : o.optionName
            }))
        ), [optionGroups]);

    // ── Handlers ────────────────────────────────────────────────────────────

    const onProductChange = React.useCallback((val) => {
        console.log('[ConfigOptionPredicate] onProductChange val:', val);
        // Reset option and value when product changes
        item.field = val ? `${CONFIG_OPTION_SENTINEL}:${val}::Raw` : CONFIG_OPTION_SENTINEL;
        item.op    = filterOps.eq;
        item.value = null;
        console.log('[ConfigOptionPredicate] after mutation item.field:', item.field);
        onItemChanged();
    }, [item, onItemChanged]);

    const onOptionChange = React.useCallback((val) => {
        // val = "optionId|optionType"
        if (!val || !decoded?.productId) return;
        const [optionId, valueType] = val.split('|');
        item.field = encodeConfigOptionField(decoded.productId, optionId, valueType);
        item.op    = filterOps.eq;
        item.value = null;
        onItemChanged();
    }, [item, decoded, onItemChanged]);

    const onOpChange = React.useCallback((val) => {
        item.op    = val;
        item.value = (val === filterOps.isempty || val === filterOps.isnotempty) ? null : item.value;
        onItemChanged();
    }, [item, onItemChanged]);

    const onValueChange = React.useCallback((val) => {
        item.value = val;
        onItemChanged(true);
    }, [item, onItemChanged]);

    // ── Derived display state ────────────────────────────────────────────────

    const selectedProductId  = decoded?.productId  ?? null;
    const selectedOptionKey  = decoded?.optionId && decoded?.valueType
        ? `${decoded.optionId}|${decoded.valueType}` : null;
    const valueType = decoded?.valueType ?? 'Raw';
    const currentOp = item.op ?? filterOps.eq;
    const showValue = currentOp !== filterOps.isempty && currentOp !== filterOps.isnotempty;

    const ops = opsForType(valueType).map(o => ({ ID: o.id, Text: o.label }));

    // Selected option's valid values (for picklist)
    const selectedOption = flatOptions.length > 0 && selectedOptionKey
        ? optionGroups.flatMap(g => g.options ?? []).find(o => o.optionID === decoded?.optionId)
        : null;
    const picklistValues = selectedOption?.values ?? [];

    // ── Value control ────────────────────────────────────────────────────────
    const renderValue = () => {
        if (!showValue) return null;
        switch (valueType) {
            case 'Numeric':
                return <AccentNumberUnbound defaultValue={item.value} onChange={onValueChange} disabled={readOnly} />;
            case 'Boolean':
                return <AccentCheckUnbound defaultValue={item.value} onChange={e => onValueChange(e.checked)} disabled={readOnly} />;
            case 'Picklist':
                if (picklistValues.length > 0) {
                    return (
                        <AccentComboUnbound
                            data={picklistValues.map(v => ({ ID: v.id, Text: v.label }))}
                            displayField="Text"
                            valueField="ID"
                            defaultValue={item.value}
                            onChange={onValueChange}
                            defaultSingle
                        />
                    );
                }
                return <AccentTextUnbound defaultValue={item.value} onChange={onValueChange} disabled={readOnly} />;
            default:
                return <AccentTextUnbound defaultValue={item.value} onChange={onValueChange} disabled={readOnly} />;
        }
    };

    return (
        <Row className="acc-qry-pred"
            onMouseEnter={() => setActionsVisible(true)}
            onMouseLeave={() => setActionsVisible(false)}>

            {/* Product selector — key includes products.length so it remounts once data is available
                and correctly reflects defaultValue even after a full component remount */}
            <Col md={2}>
                <div style={{ fontSize: '0.7em', color: '#888', marginBottom: 1 }}>Product</div>
                <AccentComboUnbound
                    key={`prod_${selectedProductId ?? 'none'}_${products.length}`}
                    data={products.map(p => ({ ID: p.ID, Text: p.Name }))}
                    displayField="Text"
                    valueField="ID"
                    defaultValue={selectedProductId}
                    onChange={onProductChange}
                    disabled={readOnly}
                />
            </Col>

            {/* Option selector */}
            <Col md={3}>
                <div style={{ fontSize: '0.7em', color: '#888', marginBottom: 1 }}>Option</div>
                {selectedProductId ? (
                    optionsLoading
                        ? <span style={{ fontSize: '0.8em', color: '#aaa' }}>Loading…</span>
                        : flatOptions.length > 0
                            ? <AccentComboUnbound
                                key={`opt_${selectedProductId}`}
                                data={flatOptions}
                                displayField="Text"
                                valueField="ID"
                                defaultValue={selectedOptionKey}
                                onChange={onOptionChange}
                                disabled={readOnly}
                            />
                            : <span style={{ fontSize: '0.8em', color: '#aaa' }}>No options available</span>
                ) : (
                    <span style={{ fontSize: '0.8em', color: '#aaa' }}>Select product first</span>
                )}
            </Col>

            {/* Operator */}
            <Col md={2}>
                <AccentComboUnbound
                    data={ops}
                    displayField="Text"
                    valueField="ID"
                    defaultValue={currentOp}
                    onChange={onOpChange}
                    hideClear
                    defaultSingle
                    disabled={readOnly || !selectedOptionKey}
                />
            </Col>

            {/* Value */}
            <Col md={4}>
                {selectedOptionKey && renderValue()}
            </Col>

            {/* Delete */}
            <Col md={1}>
                {actionsVisible && (
                    <AccentButton tagName="qDelOptItm" bsStyle="link" disabled={readOnly} outline={false} onClick={onDelete}>
                        <AccentIcons.Delete top="5" />
                    </AccentButton>
                )}
            </Col>
        </Row>
    );
});
